import { useCallback, useEffect, useRef, useState } from 'react';
import { PERMISSION_DENIED, geolocationSupported as supported, watchGeolocationPermission } from './geolocationPermission';
// Where the device is, for a map to show, and only once the visitor has allowed it. The position
// stays in the page: nothing here sends it anywhere.
export const useDeviceLocation = () => {
    const [access, setAccess] = useState(() => supported() ? 'prompt' : 'unavailable');
    const [location, setLocation] = useState(null);
    const watchRef = useRef(null);
    const handleError = useCallback((error) => {
        if (error.code === PERMISSION_DENIED) {
            setAccess('denied');
            setLocation(null);
        }
    }, []);
    const watch = useCallback(() => {
        if (watchRef.current !== null)
            return;
        watchRef.current = navigator.geolocation.watchPosition(position => {
            setAccess('granted');
            setLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
        }, handleError, { 'enableHighAccuracy': true, 'maximumAge': 15000 });
    }, [handleError]);
    useEffect(() => {
        if (!supported())
            return undefined;
        const stop = watchGeolocationPermission(state => {
            if (state === 'granted')
                watch();
            else if (state !== null)
                setAccess(state);
        });
        return () => {
            stop();
            if (watchRef.current !== null)
                navigator.geolocation.clearWatch(watchRef.current);
            watchRef.current = null;
        };
    }, [watch]);
    // Asks the browser, which asks the visitor the first time; resolves to null if they say no.
    const request = useCallback(() => new Promise(resolve => {
        if (!supported())
            return resolve(null);
        navigator.geolocation.getCurrentPosition(position => {
            const found = { lat: position.coords.latitude, lng: position.coords.longitude };
            setAccess('granted');
            setLocation(found);
            watch();
            resolve(found);
        }, error => {
            handleError(error);
            resolve(null);
        }, { 'enableHighAccuracy': true, 'timeout': 15000 });
    }), [watch, handleError]);
    return { access, location, request };
};
